import { isValid, parse } from "date-fns";
import {
  type CurriedFunction2,
  contains,
  curry,
  identity,
  is,
  isNil,
  map,
  mapObjIndexed,
  partial,
} from "ramda";

function isHash(o: any): boolean {
  return is(Object, o) && !Array.isArray(o) && !(typeof o === "function");
}

function validateDatePattern(rawValue: any, parsedValue: any): void {
  if (!isValid(parsedValue)) {
    /* eslint-disable no-console */
    console.group("Invalid date format detected:");
    console.log(`value: ${rawValue}`);
    console.log(`parsed to: ${parsedValue}`);
    console.groupEnd();
    /* eslint-enable no-console */
  }
}

interface IParsers {
  decimal: { parser: (num: string) => number };
  integer: { parser(num: string): number };
  date: { parser(date: string): Date };
  string: { parser: (a: string) => string };
  boolean: { parser: (a: boolean) => boolean };
  any: { parser: (a: any) => any };
  [key: string]: any;
}

const parsers: IParsers = {
  decimal: { parser: parseFloat },
  integer: {
    parser(num: string) {
      return parseInt(num, 10);
    },
  },
  date: {
    parser(date: string) {
      return parse(date);
    },
  },
  string: { parser: identity },
  boolean: { parser: identity },
  any: { parser: identity },
};

function parseValue(type: string, value: any) {
  const parsedValue = parsers[type].parser(value);
  const validPatterns = parsers[type].validPatterns;

  if (parsedValue && validPatterns) {
    if (type === "date") {
      validateDatePattern(value, parsedValue);
    }
  }

  return parsedValue;
}

const permittedTypes: string[] = [
  "decimal",
  "integer",
  "date",
  "string",
  "boolean",
  "any",
];

export enum ParserType {
  "DECIMAL" = "decimal",
  "INTEGER" = "interger",
  "DATE" = "date",
  "STRING" = "string",
  "BOOLEAN" = "boolean",
  "ANY" = "any",
}

interface ISchema {
  [key: string]: ParserType | ParserType[] | ISchema | ISchema[];
}

interface IParseableObject {
  [key: string]: any;
}

type IParsedResult = IParseableObject | IParseableObject[];

// parse a nested object deeply by schema.
//   {
//     foo: ParserType.STRING,
//     bar: ParserType.STRING,
//     baz: [ParserType.DATE],
//     beep: {
//       a: ParserType.DECIMAL,
//       b: [{x: ParserType.INTEGER}]
//     }
//   }

const parseBySchema: CurriedFunction2<ISchema, IParsedResult, any> = curry(
  (
    schema: ISchema | string,
    parseableObj: IParseableObject | IParseableObject[]
  ) => {
    if (isNil(schema)) {
      /* eslint-disable no-console */
      console.group("Schema missing!");
      console.log(`schema: ${schema}`);
      console.log(`object: ${parseableObj}`);
      console.groupEnd();
      /* eslint-enable no-console */
      return parseableObj;
    }

    if (Array.isArray(parseableObj)) {
      return map(
        (item: IParseableObject) => parseBySchema(schema as ISchema, item),
        parseableObj
      );
    } else if (isHash(parseableObj)) {
      return mapObjIndexed(
        (type: string | ISchema | ISchema[], key: string) => {
          const currentVal = parseableObj[key];
          if (Array.isArray(type) && Array.isArray(currentVal)) {
            return map(partial(parseBySchema, [type[0]]), currentVal);
          } else if (isHash(type) && isHash(currentVal)) {
            return parseBySchema(type as ISchema, currentVal);
          } else if (
            is(String, type) &&
            contains(type, permittedTypes) &&
            !isNil(currentVal)
          ) {
            return parseValue(type as string, currentVal);
          } else {
            return currentVal;
          }
        },
        schema
      );
    } else if (!isNil(parseableObj)) {
      return parsers[schema as string].parser(parseableObj);
    }

    return parseableObj;
  }
);

export default parseBySchema;
